import * as React from 'react';

import * as api from '@gitbook/api-client';
import { DialogBody, LoadingPanel } from '@gitbook/spine-react';

import { IntegrationDialogAside, integrationNeedsConfiguration } from '../../../Components';
import { ConfigurationStep } from './Steps/ConfigurationStep';
import { InstallInContentStep } from './Steps/InstallInContentStep';
import { OrgConfigurationStep } from './Steps/OrgConfigurationStep';
import { PickContentToConfigureStep } from './Steps/PickContentToConfigureStep';
import type { IntegrationInstallationFlowProps, IntegrationInstallationFlowStep } from './types';

/**
 * Dialog content with flow to install and configure an integration.
 */
export function IntegrationInstallationFlow(
    props: IntegrationInstallationFlowProps & {
        /** Callback when the flow is done. */
        onComplete: () => void;

        /** Initialize the flow at a specific step, e.g configuration step */
        stepState?: IntegrationInstallationFlowStep;

        /** Skip the aside layout */
        skipAside?: boolean;
    }
) {
    const { organizationId, integration, installation, onComplete, skipAside = false } = props;

    const initialStep =
        props.stepState ??
        (integration.target === api.IntegrationTarget.Organization
            ? { step: 'configure_org_installation' }
            : { step: 'install_in_content' });

    const [currentStep, setCurrentStep] =
        React.useState<IntegrationInstallationFlowStep>(initialStep);

    return (
        <div className="flex max-md:flex-col">
            {!skipAside && <IntegrationDialogAside integration={integration} />}

            <DialogBody>
                <React.Suspense fallback={<LoadingPanel size="large" className="my-auto h-full" />}>
                    {currentStep.step === 'configure_org_installation' ? (
                        <OrgConfigurationStep
                            organizationId={organizationId}
                            integration={integration}
                            installation={installation}
                            onNext={onComplete}
                        />
                    ) : currentStep.step === 'install_in_content' ? (
                        <InstallInContentStep
                            integration={integration}
                            installation={installation}
                            organizationId={organizationId}
                            onNext={({ installations, type }) => {
                                // First check that the integration/installations needs any
                                // configuration at all
                                if (!integrationNeedsConfiguration(integration)) {
                                    onComplete?.();
                                    return;
                                }

                                // if only one installation, skip the pick step
                                if (installations.length === 1) {
                                    setCurrentStep({
                                        step: 'configure_content_installation',
                                        context:
                                            type === 'space'
                                                ? {
                                                      type: 'space',
                                                      installation: installations[0],
                                                  }
                                                : {
                                                      type: 'site',
                                                      installation: installations[0],
                                                  },
                                    });
                                    return;
                                }

                                setCurrentStep({
                                    step: 'pick_content_to_configure',
                                    context:
                                        type === 'space'
                                            ? { type: 'space', installations }
                                            : { type: 'site', installations },
                                });
                            }}
                        />
                    ) : currentStep.step === 'pick_content_to_configure' ? (
                        <PickContentToConfigureStep
                            organizationId={organizationId}
                            integration={integration}
                            installation={installation}
                            context={currentStep.context}
                            onNext={(context) => {
                                setCurrentStep({
                                    step: 'configure_content_installation',
                                    context,
                                });
                            }}
                        />
                    ) : currentStep.step === 'configure_content_installation' ? (
                        <ConfigurationStep
                            organizationId={organizationId}
                            integration={integration}
                            installation={installation}
                            context={currentStep.context}
                            onNext={onComplete}
                        />
                    ) : null}
                </React.Suspense>
            </DialogBody>
        </div>
    );
}
